// Copyright (C) 2022 TANNER AG

import React from "react";
import Box from "@mui/material/Box";
import {CodeItemContentSection} from "../../../graphql";
import ContentWrapper from "../../../components/content/ContentWrapper";
import ContentHeading from "../../../components/content/ContentHeading";
import ContentRichText from "../../../components/content/ContentRichtText";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import homeCardDefaultImage from "../home_card_default_image.png";
import useAsset from "../../../hooks/useasset";
import {useNavigate} from "react-router-dom";
import {codeCatalogRoutes, routes} from "../../../lib/utils";
import Stack from "@mui/material/Stack";

type Props = {
    sections?: CodeItemContentSection;
    className?: string;
};

const CodeDetailsContent: React.FC<Props> = ({sections, className}) => {
    const navigate = useNavigate();
    const {createImageUrl} = useAsset();
    const renderCustomerBenefitsContent = (section?: CodeItemContentSection) => {
        if (section?.customerBenefits) {
            return (
                <Box id={`${section?.customerBenefits.no}`} data-section-id={`${section?.customerBenefits.no}`}
                     sx={{breakInside: "avoid", pageBreakInside: "avoid"}}>
                    <ContentHeading content={section?.customerBenefits?.title}/>
                    <ContentRichText content={section?.customerBenefits?.content}/>
                </Box>
            );
        }
    }

    const renderKeyFactsContent = (section?: CodeItemContentSection) => {
        if (section?.keyFacts) {
            return (
                <Box id={`${section?.keyFacts.no}`} data-section-id={`${section?.keyFacts.no}`}
                     sx={{breakInside: "avoid", pageBreakInside: "avoid"}}>
                    <ContentHeading content={section?.keyFacts?.title}/>
                    <ContentRichText content={section?.keyFacts?.content}/>
                </Box>
            );
        }
    }

    const renderDescriptionContent = (section?: CodeItemContentSection) => {
        if (section?.description) {
            return (
                <Box id={`${section?.description.no}`} data-section-id={`${section?.description.no}`}
                     sx={{my: 5, breakInside: "avoid", pageBreakInside: "avoid"}}>
                    <ContentHeading content={section?.description?.title}/>
                    <ContentRichText content={section?.description?.content}/>
                </Box>
            );
        }
    }

    const renderUsageNoteContent = (section?: CodeItemContentSection) => {
        if (section?.usageNotes) {
            return (
                <Box id={`${section?.usageNotes.no}`} data-section-id={`${section?.usageNotes.no}`}
                     sx={{my: 5, breakInside: "avoid", pageBreakInside: "avoid"}}>
                    <ContentHeading content={section?.usageNotes?.title}/>
                    <ContentRichText content={section?.usageNotes?.content}/>
                </Box>
            );
        }
    }

    const renderRelatedCodesContent = (section: CodeItemContentSection) => {
        if (section.relatedCodes) {
            return (
                <Box className={'relatedCodeContent'} id={`${section?.relatedCodes.no}`}
                     data-section-id={`${section?.relatedCodes.no}`}>
                    <ContentHeading content={section?.relatedCodes?.title}/>
                    <Box sx={{pl: 2}}/>
                    <Grid item container direction={"row"} spacing={2}>
                        {section.relatedCodes?.items?.map((relatedCode) => (
                            <Grid item xs={3} key={relatedCode?.id} sx={{height: 300}}
                                  onClick={() => navigate(`${routes.codeCatalog.root}${codeCatalogRoutes.details}/${relatedCode?.id}`)}>
                                <Card sx={{width: "100%", height: "100%", p: 1, cursor: "pointer"}}>
                                    <Stack spacing={1}>
                                        <Box
                                            component="img"
                                            alt={relatedCode?.no}
                                            src={relatedCode?.imagePath ? createImageUrl(relatedCode?.imagePath.replace("/image?path=", "")) : homeCardDefaultImage}
                                            sx={{ width: "100%", height: 110, objectFit: "cover", display: "block", borderRadius: 1 }}
                                        />
                                        <Typography
                                            variant="h4"
                                            sx={{
                                                whiteSpace: "pre-wrap",
                                                lineHeight: 1,
                                                fontSize: "23px !important",
                                            }}
                                        >
                                            {relatedCode?.no}
                                        </Typography>
                                        <Typography
                                            variant="h5"
                                            sx={{
                                                whiteSpace: "pre-wrap",
                                                fontSize: "16px !important",
                                                fontWeight: "bold",
                                                color: "text.secondary",
                                                lineHeight: 1,
                                                py: 1
                                            }}
                                        >
                                            {relatedCode?.subtitle}
                                        </Typography>
                                    </Stack>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            )
        }
    }
    return (
        <Box sx={{pb: 2}} className={className}>
            <ContentWrapper>
                {sections?.customerBenefits?.content !== "" && renderCustomerBenefitsContent(sections)}
                {sections?.keyFacts?.content !== "" && renderKeyFactsContent(sections)}
                {sections?.description?.content !== "" && renderDescriptionContent(sections)}
                {sections?.usageNotes?.content !== "" && renderUsageNoteContent(sections)}
                {!!sections?.relatedCodes.items.length && renderRelatedCodesContent(sections)}
            </ContentWrapper>
        </Box>
    );
};

export default CodeDetailsContent;
